import { Component, OnInit, ViewChild, OnDestroy } from "@angular/core";
import { ActivatedRoute, Params } from "@angular/router";
import { ICountry } from "../../interfaces/icountry";
import { IComparisonCountry } from "../../interfaces/icomparisoncountry";
import { IItemDetail, IItemConfig } from "../../interfaces/iconfigurationset";
import { DownloadFileComponent } from "../downloadfile/downloadfile.component";
import { CountryComparisonService } from "../../services/countrycomparison.service";
import { DownloadService } from "../../services/download.service";
import { Features } from "../../enums/features";
import { IMultiCountry } from "../../interfaces/imulticountry";
import { Subscription, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from "rxjs/operators";

@Component({
    selector: "country-comparison",
    templateUrl: "./countrycomparison.component.html",
    standalone: false
})
export class CountryComparisonComponent implements OnInit, OnDestroy {
    public countries: ICountry[] = [];
    public primaryCountry: any;
    public secondaryCountryItemDetails: any = new Array();
    public tertiaryCountryItemDetails: any = new Array();

    // Array-based approach for dropdowns
    public countryCodes: (string | null)[] = [];

    downloadingContent: boolean = false;
    features = Features;
    isLoading: boolean = false;
    @ViewChild("download") fileDownload!: DownloadFileComponent;
    @ViewChild("multiCountry") multiCountryModal!: any;
    multiCountries!: IMultiCountry[];

    // Add subscription to track and cancel pending API calls
    private comparisonSubscription!: Subscription;
    private debounceTimer: any;
    private comparisonSubject = new Subject<string[]>();
    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.downloadingContent = false;
        this.activatedRoute.snapshot.data['countries'].map((country: ICountry) => {
            const dropDownItem: any = {
                id: country.code,
                text: country.shortName,
                flagClass: "flag flag--" + country.code.toLowerCase()
            };
            this.countries.push(dropDownItem);
        });
        this.activatedRoute.params.subscribe((params: Params) => {
            this.multiCountries = this.activatedRoute.snapshot.data['multicountry'];
        });

        // Initialize array with 3 null slots (use null instead of undefined for better ng-select compatibility)
        this.countryCodes = [null, null, null];

        // Set up reactive comparison stream with debouncing and switchMap to cancel previous requests
        this.comparisonSubject.pipe(
            debounceTime(100), // Debounce to prevent rapid successive calls
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)), // Only proceed if array actually changed
            switchMap(selectedCountries => {
                this.isLoading = true;
                return this.countryComparisonSvc.getCountryComparison(selectedCountries);
            }),
            takeUntil(this.destroy$) // Automatically unsubscribe when component is destroyed
        ).subscribe(
            c => {
                try {
                    if (c && c.length > 0) {
                        this.primaryCountry = c[0];
                        this.secondaryCountryItemDetails = c.length > 1 && c[1] ? this.getItemDetails(c[1].itemConfigs) : "";
                        this.tertiaryCountryItemDetails = c.length > 2 && c[2] ? this.getItemDetails(c[2].itemConfigs) : "";

                        if (this.countryCodes[0] == null && this.countryCodes[1] == null && this.countryCodes[2] == null) {
                            // If all country codes are null, reset primary country and item details
                            this.primaryCountry = this.secondaryCountryItemDetails = this.tertiaryCountryItemDetails = null;

                        }
                    }
                } finally {
                    setTimeout(() => {
                        this.isLoading = false;
                    }, 300);
                }
            },
            error => {
                setTimeout(() => {
                    this.isLoading = false;
                }, 300);
            }
        );
    }

    constructor(
        private activatedRoute: ActivatedRoute,
        private countryComparisonSvc: CountryComparisonService,
        private downloadSvc: DownloadService) {

    }

    downloadFile(): void {
        this.downloadingContent = true;
        // Filter out invalid country codes before download - type guard ensures string[]
        const selectedCodes = this.countryCodes.filter((code): code is string =>
            code !== null && code !== undefined && code !== "");
        this.downloadSvc.downloadCountryComparison(selectedCodes).subscribe((res: any) => {
            var date: Date = new Date(Date.now());
            var dateString: string = date.getFullYear() + "-" + ("0" + (date.getMonth() + 1)).slice(-2) + "-" + ("0" + date.getDate()).slice(-2);
            var comparisons: string = selectedCodes[1] || "";
            comparisons += selectedCodes[2] ? "_" + selectedCodes[2] : "";
            this.fileDownload.downloadFile(`${selectedCodes[0]}_${comparisons}_comparison_${dateString}`, res, "pdf", "application/octet-stream");
            this.downloadingContent = false;
        }, () => {
            this.downloadingContent = false;
        });
    }

    enableDownload(): boolean {
        let selectedCountries: number = 0;

        for (let i: number = 0; i < this.countryCodes.length; ++i) {
            const code = this.countryCodes[i];
            if (code !== null && this.isValidCountrySelection(code)) {
                ++selectedCountries;
            }
        }
        return selectedCountries >= 2;
    }

    getItemDetails(configItems: IItemConfig[]): IItemDetail[] {
        const itemDetails: any[] = new Array();
        this.buildItemDetailsToList(configItems, itemDetails);
        return itemDetails;
    }

    buildItemDetailsToList(configItems: IItemConfig[], listToBuild: IItemDetail[]): void {
        if (configItems && configItems.length > 0) {
            configItems.forEach((c: IItemConfig) => {
                if (c.itemDetail && c.itemDetail.value && c.itemDetail.value.length > 0) {
                    listToBuild.push(c.itemDetail);
                }
                this.buildItemDetailsToList(c.children, listToBuild);
            });
        }
    }

    countrySelectionChange(countryCode: string, index: number): void {
        if (this.isValidCountrySelection(countryCode)) {
            // Set the country code at the appropriate index
            this.countryCodes[index] = countryCode;
            this.runComparison();
        } else {
            // Handle when country is cleared (countryCode is null/undefined)
            this.onRemoved(index);
        }
    }

    runComparison(): void {
        // Prepare the country codes array
        let selectedCountries: string[] = [];
        this.countryCodes.forEach(code => {
            if (code == null || code == undefined) {
                code = "";
            }
            selectedCountries.push(code);
        });

        // Emit the new country selection to the reactive stream
        this.comparisonSubject.next(selectedCountries);
    }

    onRemoved(index: number): void {
        this.countryCodes[index] = null;
        switch (index) {
            case 0:
                this.primaryCountry = null;
                break;
            case 1:
                this.secondaryCountryItemDetails = null;
                break;
            case 2:
                this.tertiaryCountryItemDetails = null;
                break;
            default:
                break;
        }
        this.runComparison();
    }

    multipleCountryDownloadPDF() {
        this.multiCountryModal.Open();
    }

    // Helper method to get selected country details for debugging
    getSelectedCountryDetails(index: number): any {
        const countryCode = this.countryCodes[index];
        if (!countryCode) return null;

        const country = this.countries.find(c => String(c.id) === String(countryCode));
        return country;
    }

    // Helper method to get the selected country object for display purposes
    getSelectedCountryForDisplay(index: number): any {
        const countryCode = this.countryCodes[index];
        if (!countryCode) return null;

        return this.countries.find(c => String(c.id) === String(countryCode)) || null;
    }

    // Helper method to check if a dropdown should be disabled
    isDropdownDisabled(index: number): boolean {
        if (index === 0) return false; // First dropdown is never disabled

        if (index === 1 || index === 2) {
            // Second and third dropdowns are only disabled if ALL 3 dropdowns are not selected
            return !this.countryCodes[0] && !this.countryCodes[1] && !this.countryCodes[2];
        }

        return false; // Default case
    }

    // Helper method to validate country selection
    isValidCountrySelection(countryCode: string | null): boolean {
        return countryCode !== null && countryCode !== undefined && countryCode !== "";
    }

    ngOnDestroy(): void {
        // Signal all subscriptions to complete
        this.destroy$.next();
        this.destroy$.complete();

        // Clean up any pending subscription to prevent memory leaks
        if (this.comparisonSubscription) {
            this.comparisonSubscription.unsubscribe();
        }

        // Clear any pending debounce timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }
}